import { COMPANY, SERVICE_AREAS, SERVICES, FAQ_ITEMS, PUBLIC_EMAIL } from "@/lib/constants";

// Types for schema configuration
export interface BreadcrumbItem {
  name: string;
  url: string;
}

interface SchemaGeneratorProps {
  pageType?: "home" | "service" | "about" | "contact" | "blog" | "faq" | "projects";
  breadcrumbs?: BreadcrumbItem[];
  includeFaq?: boolean;
  includeLocalBusiness?: boolean;
  includeReviews?: boolean;
  customSchema?: object;
}

// Base Local Business Schema
export const generateLocalBusinessSchema = () => ({
  "@context": "https://schema.org",
  "@type": "RoofingContractor",
  "@id": "https://obriencontractingusa.com/#business",
  name: COMPANY.name,
  description: `${COMPANY.name} - ${COMPANY.tagline}. Licensed roofing contractor serving South Florida for over ${COMPANY.yearsExperience} years.`,
  url: "https://obriencontractingusa.com",
  telephone: COMPANY.phone,
  ...(PUBLIC_EMAIL ? { email: PUBLIC_EMAIL } : {}),
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: COMPANY.address.street,
    addressLocality: COMPANY.address.city,
    addressRegion: COMPANY.address.state,
    postalCode: COMPANY.address.zip,
    addressCountry: "US"
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 27.0446,
    longitude: -82.2359
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "07:00",
      closes: "18:00"
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: "Saturday",
      opens: "08:00",
      closes: "14:00"
    }
  ],
  areaServed: SERVICE_AREAS.map(area => ({
    "@type": "AdministrativeArea",
    name: area.name
  })),
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Roofing Services",
    itemListElement: SERVICES.slice(0, 6).map((service, index) => ({
      "@type": "Offer",
      position: index + 1,
      itemOffered: {
        "@type": "Service",
        name: service.name,
        description: service.description
      }
    }))
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: COMPANY.googleRating,
    reviewCount: COMPANY.googleReviews,
    bestRating: 5,
    worstRating: 1
  },
  sameAs: [
    COMPANY.social.facebook,
    COMPANY.social.instagram,
    COMPANY.social.yelp
  ]
});

// Breadcrumb Schema Generator
export const generateBreadcrumbSchema = (breadcrumbs: BreadcrumbItem[]) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbs.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: {
      "@type": "WebPage",
      "@id": item.url
    }
  }))
});

// FAQ Schema Generator
export const generateFaqSchema = (items: readonly { question: string; answer: string }[] = FAQ_ITEMS) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map(item => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer
    }
  }))
});

// Services Schema Generator
export const generateServicesSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: SERVICES.map((service, index) => ({
    "@type": "ListItem",
    position: index + 1,
    item: {
      "@type": "Service",
      name: service.name,
      description: service.description,
      provider: {
        "@type": "RoofingContractor",
        name: COMPANY.name
      },
      areaServed: {
        "@type": "State",
        name: "Florida"
      }
    }
  }))
});

// Component that returns schema scripts for Helmet
export const SchemaGenerator = ({
  breadcrumbs,
  includeFaq = false,
  includeLocalBusiness = true,
  customSchema
}: SchemaGeneratorProps) => {
  const schemas: object[] = [];

  if (includeLocalBusiness) {
    schemas.push(generateLocalBusinessSchema());
  }

  if (breadcrumbs && breadcrumbs.length > 0) {
    schemas.push(generateBreadcrumbSchema(breadcrumbs));
  }

  if (includeFaq) {
    schemas.push(generateFaqSchema());
  }

  if (customSchema) {
    schemas.push(customSchema);
  }

  return schemas;
};

export default SchemaGenerator;